import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildCommercialPatchBody, readCommercialDomainsForRow } from "./commercialProjectAcf";
import { buildExpansionPatchBody, readExpansionEditDomains } from "./expansionProjectForms";
import {
  applyCommercialDailyProgressReport,
  applyDailyProgressReport,
  dailyProgressHasContent,
  todayIsoDate,
} from "./expansionProgressUtils";
import {
  viewerHasConstructionLeadRole,
  viewerHasInspectorRole,
} from "./expansionInspectorAccess";
import { getViewerEmailFromStorage } from "./userMatrixStorage";

function resolveReportSource(reporterEmail) {
  const email = reporterEmail || getViewerEmailFromStorage();
  // Inspectors win when a user has both roles; otherwise Construction Leads stamp as crew.
  if (viewerHasInspectorRole(email)) return "inspector";
  if (viewerHasConstructionLeadRole(email)) return "crew";
  return "inspector";
}

export async function submitExpansionDailyProgress({
  projectRow,
  report,
  apiUrl,
  getAuthHeaders,
}) {
  return submitDailyProgressReport({
    projectRow,
    report,
    isCommercial: false,
    apiUrl,
    getAuthHeaders,
  });
}

export async function submitDailyProgressReport({
  projectRow,
  report,
  isCommercial = false,
  apiUrl,
  getAuthHeaders,
}) {
  if (!projectRow?.id) {
    throw new Error("Project not found.");
  }
  if (!dailyProgressHasContent(report)) {
    throw new Error(
      "Enter conduit footage, fiber footage, proofing completed, handholes installed, or progress notes."
    );
  }

  const reporterEmail = report.reporterEmail || getViewerEmailFromStorage();
  const payload = {
    ...report,
    date: report.date || todayIsoDate(),
    reporterEmail,
    reportSource: report.reportSource || resolveReportSource(reporterEmail),
  };

  let body;
  if (isCommercial) {
    const domains = readCommercialDomainsForRow(projectRow, projectRow.acf_domains);
    const nextDomains = applyCommercialDailyProgressReport(domains, payload);
    body = buildCommercialPatchBody(projectRow.acf_groups_json, nextDomains);
  } else {
    const domains = readExpansionEditDomains(projectRow, projectRow.acf_domains);
    const nextDomains = applyDailyProgressReport(domains, payload);
    body = buildExpansionPatchBody(projectRow.acf_groups_json, nextDomains);
  }

  const base = apiUrl || getApiBase();
  await axios.patch(`${base}/projects/${projectRow.id}`, body, { headers: getAuthHeaders() });
}

export function expansionDailyProgressDismissKey(scope, viewerEmail, projectId) {
  const email = String(viewerEmail || "").trim().toLowerCase();
  const date = todayIsoDate();
  if (scope === "global") return `pb.inspectorDailyProgress.${email}.${date}`;
  return `pb.expansionDailyProgress.${projectId}.${date}`;
}

export function isExpansionDailyProgressDismissed(key) {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function dismissExpansionDailyProgress(key) {
  try {
    localStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}
